import { Box, Grid, TextField, IconButton, Tooltip, Stack, Button, Typography } from "@mui/material";
import { useFieldArray, useForm } from "react-hook-form";
import { FormProvider, RHFMultipleSelectField, RHFSwitch } from "../../common/hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { apiConstants, get, post } from "../../common/constants";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import Loader from "../../common/Loader";
import {
  T, cardSx, primaryBtnSx, ghostBtnSx, fieldSx, FieldLabel,
  SectionCard, Pill, EmptyState, activeTone, ORDER_STATUS_OPTIONS,
} from "./webhookUi";

const FORMAT = "WEBENGAGE";
const BLANK = { webHookUrl: "", subscribedEvents: [], isWebHookActive: false, key: "" };

function WebEngageWebhook() {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [preserved, setPreserved] = useState([]);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        list: Yup.array().of(
          Yup.object().shape({
            webHookUrl: Yup.string().required("Webhook URL is required").url("Please enter a valid URL"),
            key: Yup.string().required("Key is required"),
          })
        ),
      })
    ),
    defaultValues: { merchant: "", list: [BLANK] },
  });
  const { handleSubmit, control, register, setValue, reset, watch, formState: { errors } } = methods;
  const merchantId = watch('merchant');
  const listValues = watch('list') || [];
  const { fields, append, remove } = useFieldArray({ control, name: "list" });

  const onSubmit = async (data) => {
    const updateList = data.list.map((item) => ({
      webHookUrl: item.webHookUrl,
      isWebHookActive: !!item.isWebHookActive,
      formatType: FORMAT,
      additionalData: [{ key: "api-key", name: "api-key", value: item.key }],
      subscribedEvents: item?.subscribedEvents?.length > 0
        ? item.subscribedEvents.map((d) => (d === "ALL" ? null : d)) : [],
    }));
    setIsSubmitting(true);
    const request = { merchantId, webHooks: [...updateList, ...preserved] };
    await post(apiConstants.WEBHOOK, request).then((res) => {
      if (res.data.responseObj === "success") enqueueSnackbar("Webengage webhook saved", { variant: 'success' });
      else enqueueSnackbar(res.data.responseObj, { variant: 'error' });
    }).catch((err) => {
      console.log(err);
      enqueueSnackbar(err?.response?.data?.message || "Could not save webhook", { variant: 'error' });
    });
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!merchantId) return;
    setIsLoading(true);
    get(`${apiConstants.WEBHOOK_SEARCH}?merchantId=${merchantId}`)
      .then((res) => {
        const all = res?.data || [];
        const mine = all.filter((v) => v.formatType === FORMAT);
        setPreserved(all.filter((v) => v.formatType !== FORMAT));
        reset({ merchant: merchantId, list: [BLANK] });
        mine.forEach((item, index) => {
          const row = {
            webHookUrl: item.webHookUrl,
            subscribedEvents: item.subscribedEvents || [],
            isWebHookActive: !!item.isWebHookActive,
            key: item?.additionalData?.[0]?.value || '',
          };
          if (index === 0) {
            setValue('list.0.webHookUrl', row.webHookUrl);
            setValue('list.0.subscribedEvents', row.subscribedEvents);
            setValue('list.0.isWebHookActive', row.isWebHookActive);
            setValue('list.0.key', row.key);
          } else {
            append(row);
          }
        });
      })
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const addBlankField = () => append({ ...BLANK });

  return (
    <>
      {isLoading && <Loader />}
      <Box>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <SectionCard
            title="Webengage Webhooks"
            subtitle="Push order updates to a merchant's Webengage integration."
            icon={<Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: T.TEAL_BG, display: 'grid', placeItems: 'center' }}><CampaignOutlinedIcon sx={{ color: T.TEAL, fontSize: 22 }} /></Box>}
            sx={{ mb: 2 }}
          >
            <FieldLabel required>Merchant</FieldLabel>
            <Box sx={{ maxWidth: 460, ...fieldSx }}>
              <MerchantFilterCached fullWidth placeholder="Select a merchant…" />
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
              <StorefrontOutlinedIcon sx={{ fontSize: 15, color: T.MUTED }} />
              <Typography sx={{ fontSize: 12, color: T.MUTED }}>Pick a merchant to manage its Webengage webhooks.</Typography>
            </Stack>
          </SectionCard>

          {!merchantId ? (
            <EmptyState
              icon={<Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: T.TEAL_BG, display: 'grid', placeItems: 'center', mx: 'auto' }}><CampaignOutlinedIcon sx={{ fontSize: 30, color: T.TEAL }} /></Box>}
              title="No merchant selected"
              subtitle="Choose a merchant above to configure its Webengage webhooks."
            />
          ) : (
            <>
              <Stack spacing={2}>
                {fields.map((field, index) => {
                  const active = !!listValues?.[index]?.isWebHookActive;
                  const eventCount = (listValues?.[index]?.subscribedEvents || []).filter(Boolean).length;
                  return (
                    <SectionCard
                      key={field.id}
                      headerBg={active ? T.GREEN_BG : T.LIGHT}
                      icon={<Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: '#fff', border: `1px solid ${T.BORDER}`, display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13, color: T.NAVY }}>{index + 1}</Box>}
                      title={`Webhook ${index + 1}`}
                      subtitle={eventCount > 0 ? `${eventCount} event${eventCount === 1 ? '' : 's'} subscribed` : 'All events'}
                      action={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Pill tone={activeTone(active)} dot>{active ? 'Active' : 'Inactive'}</Pill>
                          {fields.length > 1 && (
                            <Tooltip title="Remove this webhook">
                              <IconButton size="small" onClick={() => remove(index)} sx={{ color: T.RED, '&:hover': { bgcolor: T.RED_BG } }}>
                                <DeleteOutlineOutlinedIcon sx={{ fontSize: 19 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      }
                    >
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} md={8}>
                          <FieldLabel required>Webhook URL</FieldLabel>
                          <TextField fullWidth size="small" placeholder="https://your-endpoint.com/webhook" sx={fieldSx}
                            {...register(`list.${index}.webHookUrl`)}
                            error={Boolean(errors?.list?.[index]?.webHookUrl?.message)}
                            helperText={errors?.list?.[index]?.webHookUrl?.message} />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                          <Box sx={{ px: 1.5, py: 0.75, borderRadius: '10px', border: `1px solid ${T.BORDER}`, bgcolor: active ? T.GREEN_BG : '#F7F9FC', width: '100%' }}>
                            <RHFSwitch name={`list.${index}.isWebHookActive`} label={active ? 'Webhook active' : 'Webhook inactive'} color="success" />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FieldLabel required>API Key</FieldLabel>
                          <TextField fullWidth size="small" placeholder="api-key" sx={fieldSx}
                            {...register(`list.${index}.key`)}
                            error={Boolean(errors?.list?.[index]?.key?.message)}
                            helperText={errors?.list?.[index]?.key?.message} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FieldLabel>Subscribed Events</FieldLabel>
                          <Box sx={fieldSx}>
                            <RHFMultipleSelectField width="100%" search name={`list.${index}.subscribedEvents`} options={ORDER_STATUS_OPTIONS} label="" placeholder="Leave empty to receive all events" />
                          </Box>
                        </Grid>
                      </Grid>
                    </SectionCard>
                  );
                })}
              </Stack>

              <Button fullWidth startIcon={<AddCircleOutlineOutlinedIcon />} onClick={addBlankField} sx={{ ...ghostBtnSx, mt: 2 }}>
                Add another webhook
              </Button>

              <Box sx={{ ...cardSx, position: 'sticky', bottom: 12, mt: 2, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 -2px 12px rgba(16,24,40,0.08)' }}>
                <Typography sx={{ fontSize: 12.5, color: T.MUTED }}>
                  {fields.length} Webengage webhook{fields.length === 1 ? '' : 's'}
                  {preserved.length > 0 && ` · ${preserved.length} other integration${preserved.length === 1 ? '' : 's'} preserved`}
                </Typography>
                <LoadingButton loading={isSubmitting} variant="contained" type="submit" sx={primaryBtnSx}>Save changes</LoadingButton>
              </Box>
            </>
          )}
        </FormProvider>
      </Box>
    </>
  );
}

export default WebEngageWebhook;
